<template>
  <div class="post">
    <div class="post__main">
      <div
        class="post__image"
        :class="{
          'post__image--empty': !hasImage
        }"
        :style="{
          backgroundImage: hasImage
            ? 'url(' + post.post_data.image_url[0].sourceUrl + ')'
            : ''
        }"
      >
        <template v-if="!hasImage">
          <v-icon color="primary" :size="iconSize" class="mb-1"
            >icon-photo-add</v-icon
          >
          <p>{{ $t("message.posts.list.noPhoto") }}</p>
        </template>

        <PostListItemCategory :category-name="categoryName" />
      </div>
      <div class="post__content">
        <p
          v-if="
            post &&
              post.post_data &&
              post.post_data.event &&
              post.post_data.event.title
          "
          class="post__title"
        >
          {{ post.post_data.event.title }}
        </p>
        <p v-if="post && post.post_data && post.post_data.text">
          {{ post.post_data.text }}
        </p>

        <div v-if="durationTime" class="post__duration-time">
          <p>{{ $t("message.posts.list.durationTime") }}</p>
          <div v-if="durationTime.from">
            <span>{{ $t("message.common.from") }}</span>
            <span>{{ durationTime.from }}</span>
          </div>
          <div v-if="durationTime.to">
            <span>{{ $t("message.common.to") }}</span>
            <span>{{ durationTime.to }}</span>
          </div>
        </div>
        <a
          v-if="post && post.post_data && post.post_data.search_url"
          class="post__show-details-btn"
          :href="post.post_data.search_url"
          target="_blank"
        >
          {{ $t("message.common.seeDetails") }}
        </a>
      </div>
    </div>
    <div class="post__planned-bottom">
      <div class="planned-list">
        <div class="planned-list__label">{{ $t("message.common.status") }}</div>
        <div class="planned-list__title">
          {{ $t("message.posts.scheduledSending") }}
        </div>
        <!-- TODO set date and hour when api ready -->
        <div class="planned-list__el mt-2">
          <div class="planned-list__label">{{ $t("message.common.date") }}</div>
          <div class="planned-list__value">{{ publishAtDate }}</div>
        </div>
        <div class="planned-list__el">
          <div class="planned-list__label">{{ $t("message.common.hour") }}</div>
          <div class="planned-list__value">{{ publishAtTime }}</div>
        </div>
        <div class="planned-list__label mt-2">
          {{ $t("message.reviews.facilities") }}
        </div>
        <div class="planned-list__title d-flex">
          <PostListPlannedSelectedFacilities
            :label="getFacilities.label"
            :facility-location-type="getFacilityLocationType"
            :full-names="getFacilities.fullNames"
            :facility-type="getFacilityType"
          />
        </div>
      </div>
      <div class="post__actions">
        <div>
          <v-tooltip :disabled="enablePostEditBtn" bottom max-width="200px">
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on">
                <v-btn
                  v-bind="attrs"
                  :disabled="!enablePostEditBtn"
                  icon
                  class="mr-4"
                  :to="{
                    name: 'posts-scheduled-edit',
                    params: { id: post.id }
                  }"
                >
                  <v-icon color="black" size="24">icon-edit</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{
              $t("message.posts.planning.actionsDisabledTooltip")
            }}</span>
          </v-tooltip>
          <v-tooltip :disabled="enablePostDeleteBtn" bottom max-width="200px">
            <template v-slot:activator="{ on, attrs }">
              <span v-on="on">
                <v-btn
                  v-if="!isDeleted"
                  v-bind="attrs"
                  icon
                  :disabled="isProcessing || !enablePostDeleteBtn"
                  @click="dialog.deletePost = true"
                >
                  <v-icon color="black" size="24">icon-trash</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{
              $t("message.posts.planning.actionsDisabledTooltip")
            }}</span>
          </v-tooltip>
        </div>
        <div class="post__author">
          <div class="planned-list__label">
            {{ $t("message.posts.planning.authorAndDate") }}
          </div>
          <span>
            {{ post.created_at }}
          </span>
          <span>
            <v-tooltip bottom max-width="500px">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  {{ post.author.email }}
                </span>
              </template>
              <span> {{ post.author.email }} </span>
            </v-tooltip>
          </span>
        </div>
      </div>
    </div>
    <PostFacilitiesDialog
      :is-open="dialog.facilitiesList"
      :post-id="post.id"
      :status="chosenSendingStatus"
      @close="closeFacilitiesDialog"
    />

    <v-dialog v-model="dialog.deletePost" max-width="450">
      <v-card class="pa-6">
        <p class="text-center">
          {{ $t("message.posts.list.confirmPlannedDeleteDialog.text") }}
        </p>
        <div class="d-flex justify-center">
          <ui-button
            outlined
            class="mr-3"
            color="primary"
            @click="dialog.deletePost = false"
          >
            {{ $t("message.common.cancel") }}
          </ui-button>
          <ui-button color="primary" @click="deletePost">
            {{ $t("message.common.confirmDelete") }}
          </ui-button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import {
  POST_BY_FACILITY_STATUS,
  POSTS_CALL_TO_ACTION_TYPE
} from "@/enums/posts.enum";
import { pluralCount } from "@/helpers/helpers";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

import PostListItemCategory from "@/components/Posts/PostListItemCategory.vue";
import PostListPlannedSelectedFacilities from "@/components/Posts/PostListItem/PostListPlannedSelectedFacilities.vue";
import PostFacilitiesDialog from "@/components/Posts/dialog/PostFacilitiesDialog.vue";
import { plannedPostItemMixin } from "@/helpers/mixins/planned-post-item";
import { AccountType } from "@/enums/account-type";

export default {
  name: "PostListItem",
  components: {
    PostListItemCategory,
    PostFacilitiesDialog,
    PostListPlannedSelectedFacilities
  },
  mixins: [plannedPostItemMixin],
  data() {
    return {
      POST_BY_FACILITY_STATUS,
      FEATURE_FLAG,
      iconSize: 18,
      dialog: { deletePost: false, facilitiesList: false },
      chosenSendingStatus: null
    };
  },
  computed: {
    getFacilitiesCount() {
      return this.post.facilities.facilities_ids.length;
    },
    getFacilityLocationType() {
      if (this.post.facilities.facilities_groups) {
        return `${this.$t("message.posts.planning.groups")}`;
      }
      if (this.post.facilities.cities) {
        return `${this.$t("message.posts.planning.cities")}`;
      }
      return null;
    },
    getFacilityType() {
      if (this.post.facilities.facilities_groups) {
        return `${this.$t("message.common.groups")}(${
          this.getFacilitiesCount
        })`;
      }
      if (this.post.facilities.cities) {
        return `${this.$t("message.reviews.cities")}(${
          this.getFacilitiesCount
        })`;
      }
      return null;
    },
    getFacilities() {
      const facilitiesNames =
        this.post.facilities.facilities_groups || this.post.facilities.cities;
      let label = null;

      if (facilitiesNames) {
        label = facilitiesNames.join(", ");
      } else {
        if (this.post.facilities.facilities_all) {
          label = `${this.$t("message.permissions.all")} (${
            this.getFacilitiesCount
          })`;
        } else {
          label = `${this.$t("message.permissions.selected")} (${
            this.getFacilitiesCount
          })`;
        }
      }

      return {
        label,
        fullNames: facilitiesNames ? facilitiesNames.join(", ") : null
      };
    },
    isAdmin() {
      return this.$store.getters["company/accountType"] === AccountType.ADMIN;
    },
    isOwner() {
      return this.$store.getters["company/accountType"] === AccountType.OWNER;
    },
    enablePostEditBtn() {
      return (
        this.post.author.id ===
        this.$store.state.company.company.company_user.user_id
      );
    },
    enablePostDeleteBtn() {
      return this.enablePostEditBtn || this.isAdmin || this.isOwner;
    },
    publishAtDate() {
      return this.getDate(this.post.publish_at);
    },
    publishAtTime() {
      return this.getTime(this.post.publish_at);
    },
    categoryName() {
      return this.$t(
        `message.posts.list.${this.post && this.post && this.post.type}`
      );
    }
  },

  methods: {
    isFeatureFlagEnabled,
    deletePost() {
      const params = {
        id: this.post.id,
        numLocations: this.post.facilities_count,
        postType: this.postType,
        publishDate: this.getDate(this.post.created_at)
      };
      this.$emit("deletePost", params);
      this.dialog.deletePost = false;
    },
    showFacilitiesDialog(status) {
      this.chosenSendingStatus = status;
      this.dialog.facilitiesList = true;
    },
    closeFacilitiesDialog() {
      this.chosenSendingStatus = null;
      this.dialog.facilitiesList = false;
    },
    getDate(date) {
      return moment(date).format(FORMAT.DATE);
    },
    getTime(date) {
      return moment(date).format(FORMAT.TIME);
    },
    getFacilitiesCountLabel(count) {
      return `${pluralCount("message.pluralForms.facilityGenitive", count)}`;
    },
    getClicksLabel(count, actionType) {
      const countText = pluralCount("message.pluralForms.click", count);
      if (!actionType) {
        return countText;
      }

      const buttonText = this.$t("message.posts.list.clickInButton", {
        label: this.$t(
          `message.posts.callToAction.${POSTS_CALL_TO_ACTION_TYPE[actionType]}`
        )
      });

      return `${countText} ${buttonText}`;
    },
    getViewsCountLabel(count) {
      return `${pluralCount("message.pluralForms.view", count)}`;
    }
  }
};
</script>
<style lang="scss" scoped src="./PostListItem.scss"></style>

<style lang="scss" scoped>
.planned-list {
  width: 100%;
  overflow: hidden;

  &__el {
    display: flex;
    align-items: center;
  }
  &__label {
    @include font-size(12px);
    color: $gray;
  }
  &__title {
    @include font-size(12px);
    margin: $spacing * 0.5 0;
  }

  &__value {
    @include font-size(14px);
    font-weight: 500;
    margin-left: $spacing * 0.5;
  }
}
.post {
  display: block;

  &__actions {
    flex-shrink: 0;

    & > div {
      display: flex;
      justify-content: flex-end;
    }
  }

  &__author {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: $spacing;

    span {
      @include font-size(12px);
      text-overflow: ellipsis;
      display: block;
      white-space: nowrap;
      max-width: 230px;
      overflow: hidden;
    }
  }

  &__main {
    width: 100%;
  }
  &__planned-bottom {
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: $spacing * 8;
  }
}
</style>
